import React from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { Box, Typography, DialogActions } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import useNotifications from '../../../../_lib/notifications/useNotifications';
import CbButton from '../../../../components/Buttons/CbButton';
import TextFieldBase from '../../../../components/inputs/text-fields/base/TextFieldBase';
import { withFormController } from '../../../../components/hocs/forms';
import type { MarkSubmissionAsExcessPayload } from '../../../../api/submissions/submissions.api';
import { markSubmissionAsExcess } from '../../../../api/submissions/submissions.api';

// Wrap TextFieldBase with form controller
const TextField = withFormController(TextFieldBase);

// Form interface for display/input (uses strings)
interface MarkAsExcessFormData {
  requestedLimit: string;
  attachmentPoint: string;
  quoteNumber: string;
  description: string;
}

// Helper functions for number formatting
const formatNumberWithCommas = (value: string): string => {
  // Remove all non-digit characters
  const numbers = value.replace(/\D/g, '');
  // Add commas for thousands
  return numbers.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

const parseNumberFromFormatted = (value: string): string => {
  // Remove all non-digit characters to get raw number
  return value.replace(/\D/g, '');
};

const schema = Yup.object().shape({
  requestedLimit: Yup.string()
    .required('Requested Limit is required')
    .test(
      'is-positive-number',
      'Requested Limit must be a positive number',
      (value) => {
        if (!value) {
          return false;
        }
        const num = parseInt(parseNumberFromFormatted(value), 10);
        return !Number.isNaN(num) && num > 0;
      }
    ),
  attachmentPoint: Yup.string()
    .required('Attachment Point is required')
    .test(
      'is-positive-number',
      'Attachment Point must be a positive number',
      (value) => {
        if (!value) {
          return false;
        }
        const num = parseInt(parseNumberFromFormatted(value), 10);
        return !Number.isNaN(num) && num > 0;
      }
    ),
  quoteNumber: Yup.string().required('Quote Number is required'),
  description: Yup.string().required('Description is required'),
});

const MarkAsExcessModal = ({
  data,
  close,
}: {
  data: {
    submissionId: string;
    accountId: string;
    submissionNumber: string;
    accountName: string;
  };
  close: () => void;
}) => {
  const notifications = useNotifications();
  const queryClient = useQueryClient();
  const methods = useForm<MarkAsExcessFormData>({
    defaultValues: {
      requestedLimit: '',
      attachmentPoint: '',
      quoteNumber: '',
      description: '',
    },
    resolver: yupResolver(schema),
  });

  const {
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = methods;

  // Handle number formatting for Requested Limit
  const handleRequestedLimitChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const formatted = formatNumberWithCommas(e.target.value);
    setValue('requestedLimit', formatted);
  };

  // Handle number formatting for Attachment Point
  const handleAttachmentPointChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const formatted = formatNumberWithCommas(e.target.value);
    setValue('attachmentPoint', formatted);
  };

  const onSubmit = async (formData: MarkAsExcessFormData) => {
    try {
      // Convert formatted strings back to numbers for API
      const apiPayload: MarkSubmissionAsExcessPayload = {
        requestedLimit: parseInt(
          parseNumberFromFormatted(formData.requestedLimit),
          10
        ),
        attachmentPoint: parseInt(
          parseNumberFromFormatted(formData.attachmentPoint),
          10
        ),
        quoteNumber: formData.quoteNumber,
        description: formData.description,
      };

      await markSubmissionAsExcess(data.submissionId, {
        data: apiPayload,
      });

      notifications.success('Submission marked as excess successfully.');

      // Invalidate queries to refresh the data
      queryClient.invalidateQueries(['submissions']);

      close();
    } catch (err) {
      notifications.error('Unable to mark as excess.');
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Box mb={3} mt={2} textAlign="center">
          <Typography
            sx={{
              fontWeight: 400,
              fontSize: '1.1rem',
              color: 'text.primary',
              fontFamily: (theme) => theme.typography.fontFamily,
              mb: 2,
            }}
          >
            Are you sure you want to mark this submission as Excess?
          </Typography>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: '1.35rem',
              color: 'text.primary',
              fontFamily: (theme) => theme.typography.fontFamily,
              letterSpacing: 0.5,
              textAlign: 'center',
            }}
          >
            {data.submissionNumber}: {data.accountName}
          </Typography>
        </Box>
        <Box style={{ padding: '0 24px' }}>
          <Box display="flex" gap={2} mb={2}>
            <Box flex={1}>
              <TextField
                name="requestedLimit"
                label="Requested Limit"
                required
                placeholder="e.g., $1,000,000"
                onChange={handleRequestedLimitChange}
              />
            </Box>
            <Box flex={1}>
              <TextField
                name="attachmentPoint"
                label="Attachment Point"
                required
                placeholder="e.g., $500,000"
                onChange={handleAttachmentPointChange}
              />
            </Box>
            <Box flex={1}>
              <TextField
                name="quoteNumber"
                label="Quote Number"
                required
                placeholder="Enter quote number"
              />
            </Box>
          </Box>
          <Box mb={2}>
            <TextField
              name="description"
              label="Description / Details"
              required
              multiline
              minRows={4}
              placeholder="Provide details about the excess submission..."
              inputProps={{ maxLength: 500 }}
            />
          </Box>
          <DialogActions>
            <CbButton
              styleName="cancel"
              onClick={close}
              buttonText="Cancel"
              disabled={isSubmitting}
            />
            <CbButton
              styleName="ctaButton"
              type="submit"
              buttonText="Mark as Excess"
              loading={isSubmitting}
              disabled={isSubmitting}
            />
          </DialogActions>
        </Box>
      </form>
    </FormProvider>
  );
};

export default MarkAsExcessModal;

export const MarkAsExcessModalConfig = {
  MarkAsExcessModal: {
    component: MarkAsExcessModal,
    config: {
      title: 'Mark as Excess',
      maxWidth: 'md',
      fullWidth: true,
    },
  },
};
